import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from "@/components/ui/dialog.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Input} from "@/components/ui/input.tsx";
import {Textarea} from "@/components/ui/textarea.tsx";
import {Label} from "@/components/ui/label.tsx";
import {Check, UserPlus, X, Loader2} from "lucide-react";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/components/ui/tabs.tsx";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@/components/ui/table.tsx";
import React, {useState} from "react";
import {ClanApplication} from "@/types/ClanApplication.ts";
import {ClanMember} from "@/types/ClanMember.ts";
import {Badge} from "@/components/ui/badge.tsx";
import {FcManager} from "react-icons/fc";
import {MdManageAccounts} from "react-icons/md";
import {useQuery} from "@tanstack/react-query";
import {Session} from "@supabase/supabase-js";
import {useAuth} from "@/contexts/AuthContext.tsx";

const ClanManagementModal = (
    {
        manageClanOpen,
        setManageClanOpen,
        clanApplications,
        handleApproveRequest,
        handleRejectRequest,
        handleKickMember,
        members,
        membersRefetch,
        applicationsRefetch,
        isSaving,
        setIsSaving,
        minPoints,
        setMinPoints,
        description,
        setDescription,
        clanId
    }: {
        manageClanOpen: boolean,
        setManageClanOpen: React.Dispatch<React.SetStateAction<boolean>>,
        clanApplications: ClanApplication[],
        handleApproveRequest: (id: string) => void,
        handleRejectRequest: (id: string) => void,
        handleKickMember: (id: string) => void,
        members: ClanMember[],
        membersRefetch: () => void,
        applicationsRefetch: () => void,
        isSaving: boolean,
        setIsSaving: React.Dispatch<React.SetStateAction<boolean>>,
        minPoints: string,
        setMinPoints: React.Dispatch<React.SetStateAction<string>>,
        description: string,
        setDescription: React.Dispatch<React.SetStateAction<string>>,
        clanId: string
    }) => {
    const [activeTab, setActiveTab] = useState('requests');
    // const [minPoints, setMinPoints] = useState<string>("");
    // const [description, setDescription] = useState<string>("");
    /*const [userSession] = useState<Session | null>(() => {
        const cachedSession = localStorage.getItem('session');
        return cachedSession ? JSON.parse(cachedSession) : null;
    });*/
    const {user, session, loading} = useAuth();

    const formatNumber = (num: number) => {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const {data: clanData, refetch: clanRefetch, isLoading: isClanLoading} = useQuery({
        queryKey: ['clanData', session],
        queryFn: async () => {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/clans/${clanId}`, {
                method: 'GET',
                headers: {
                    "Authorization": `Bearer ${session.access_token}`
                }
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const responseData = await response.json();
            setDescription(responseData.description);
            setMinPoints(parseInt(responseData.joinCriteria.value).toString());
            // console.log("clan response:", responseData);
            return responseData;
        },
        retry: 1,
        retryDelay: 1000
    });
    const handleSaveSettings = async () => {
        if (description == "" || minPoints == null) {
            return;
        }
        setIsSaving(true);
        const saveSettings = await fetch(`${import.meta.env.VITE_API_URL}/clans/${clanId}/manage/settings`, {
            method: 'PATCH',
            headers: {
                "Authorization": `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
                description,
                joinCriteria: {type: "points", value: parseInt(minPoints)}
            })
        });
        setIsSaving(false);
        const saveSettingsResponse = await saveSettings.json();
        if (saveSettingsResponse?.success) {
            await clanRefetch();
        }
        console.log("Saving clan settings:", saveSettingsResponse);

    };
    return (
        <Dialog open={manageClanOpen} onOpenChange={setManageClanOpen}>
            <DialogTrigger asChild>
                <div className="relative inline-block">
                    <Button variant="amber" className="whitespace-nowrap" onClick={() => {
                        membersRefetch();
                        applicationsRefetch();
                    }}>
                        <MdManageAccounts className="h-4 w-4 mr-1"/>
                        Manage Clan
                    </Button>
                    {clanApplications?.length > 0 && (
                        <span
                            className="absolute top-[-6px] right-[-6px] flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs text-white shadow-md">
                                {clanApplications?.length}
                         </span>
                    )}

                </div>
            </DialogTrigger>
            <DialogContent
                className="max-w-3xl w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle>{clanData?.name || "test"}</DialogTitle>
                    <DialogDescription>
                        {clanData?.description}
                        {/*Manage membership requests and current members of your clan*/}
                    </DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="requests" className="mt-4 flex-1 flex flex-col">
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="requests"
                                     onClick={() => setActiveTab('requests')}>Pending
                            Requests</TabsTrigger>
                        <TabsTrigger value="members"
                                     onClick={() => setActiveTab('members')}>Current
                            Members</TabsTrigger>
                        <TabsTrigger value="settings"
                                     onClick={() => setActiveTab('settings')}>Settings</TabsTrigger>
                    </TabsList>

                    <TabsContent value="requests"
                                 className="mt-4 flex-1 overflow-hidden">
                        <div
                            className="h-[50vh] sm:h-[400px] w-full overflow-x-auto dark:bg-[#1F2937]">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Username</TableHead>
                                        <TableHead className="text-justify">Total
                                            bp</TableHead>
                                        <TableHead
                                            className="text-justify">Success Rate</TableHead>
                                        {/*<TableHead>Badges</TableHead>*/}
                                        <TableHead
                                            className="text-justify">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(clanApplications && clanApplications.length > 0) ? clanApplications?.map((request) => (
                                        <TableRow key={request.id}>
                                            <TableCell
                                                className="font-medium">@{request.username}</TableCell>
                                            <TableCell
                                                className="text-justify">{formatNumber(request.totalPollPoints)}</TableCell>
                                            <TableCell
                                                className="text-justify">{request.successRate}</TableCell>
                                            {/*<TableCell>
                                                <div className="flex space-x-1">
                                                    {request.badges.map((badge, idx) => (
                                                        <span key={idx}>{badge}</span>
                                                    ))}
                                                </div>
                                            </TableCell>*/}
                                            <TableCell className="text-justify">
                                                <div
                                                    className="flex justify-end space-x-2">
                                                    <Button
                                                        variant="default"
                                                        size="sm"
                                                        onClick={() => handleApproveRequest(request.userId)}
                                                    >
                                                        <Check className="h-4 w-4"/>
                                                    </Button>
                                                    <Button
                                                        variant="destructive"
                                                        size="sm"
                                                        onClick={() => handleRejectRequest(request.userId)}
                                                    >
                                                        <X className="h-4 w-4"/>
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow key={"empty"}>
                                            <TableCell colSpan={999} className={"w-full text-center px-2 mx-2"}>
                                                There is nothing to see here!
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="members"
                                 className="mt-4 flex-1 overflow-hidden">
                        <div
                            className="h-[50vh] sm:h-[400px] w-full overflow-x-auto dark:bg-[#1F2937]">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="text-center">Username</TableHead>
                                        <TableHead className="text-center">Total
                                            bp</TableHead>
                                        <TableHead
                                            className="text-center">Success Rate</TableHead>
                                        <TableHead className="text-center">Badges</TableHead>
                                        <TableHead
                                            className="text-center">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(members && members.length > 0) && members?.sort((a, b) => a.role == 'owner' ? 0 : 1).map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell
                                                className="text-center font-medium">@{user.username}</TableCell>
                                            <TableCell
                                                className="text-center">{formatNumber(user.totalPollPoints)}</TableCell>
                                            <TableCell
                                                className="text-center">{user.successRate}</TableCell>
                                            <TableCell className="text-center">
                                                <Badge
                                                    key={user.userId}
                                                    variant={user.role == 'owner' ? "default" : "secondary"}
                                                    className={`${user.role == 'owner' ? 'text-white' : 'text-black'} mx-auto text-white dark:text-white dark:border-gray-600`}
                                                >
                                                    {user.role}
                                                </Badge>
                                            </TableCell>
                                            {/*<TableCell>
                                                <div className="flex space-x-1">
                                                    {user.badges.map((badge, idx) => (
                                                        <span key={idx}>{badge}</span>
                                                    ))}
                                                </div>
                                            </TableCell>*/}
                                            <TableCell className="text-center">
                                                {user.role != 'owner' && (<Button
                                                    variant="destructive"
                                                    size="sm"
                                                    onClick={() => {
                                                        handleKickMember(user.userId)
                                                    }}
                                                >
                                                    <X className="h-4 w-4"/>
                                                    Kick
                                                </Button>)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>
                    <TabsContent value="settings"
                                 className="mt-4 flex-1 overflow-hidden">
                        <div
                            className="h-[50vh] sm:h-[400px] w-full overflow-y-auto dark:bg-[#1F2937] p-4 md:p-6 flex flex-col">
                            <div className="space-y-4 flex-grow">
                                <div className="space-y-1.5">
                                    <Label htmlFor="minPoints">Min Points</Label>
                                    <Input
                                        id="minPoints"
                                        type="number"
                                        value={minPoints}
                                        onChange={(e) => setMinPoints(e.target.value)}
                                        placeholder="e.g., 1000"
                                        className="dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:ring-amber-500 focus:border-amber-500"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="description">Description</Label>
                                    <Textarea
                                        id="description"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        maxLength={150}
                                        placeholder="Short clan description (max 50 chars)"
                                        className="dark:bg-gray-700 dark:text-white dark:border-gray-600 resize-none focus:ring-amber-500 focus:border-amber-500"
                                        rows={3}
                                    />
                                    <p className="text-xs text-muted-foreground text-right pr-1">
                                        {description.length}/150
                                    </p>
                                </div>
                            </div>
                            <div
                                className="flex justify-end pt-4 mt-auto"> {/* Button container, mt-auto pushes it down if space allows */}
                                <Button
                                    onClick={handleSaveSettings}
                                    disabled={isSaving || (!minPoints || !description)} // Optionally disable if fields are empty
                                    variant="amber"
                                    className="w-full sm:w-auto"
                                >
                                    {isSaving ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin"/>
                                            Saving...
                                        </>
                                    ) : (
                                        "Save Changes"
                                    )}
                                </Button>
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    )
}

export default ClanManagementModal;
